<script>
	import CustomBreadcrumb from "src/components/CustomBreadcrumb/CustomBreadcrumb.svelte";
	import ErrorMessageHandler from "src/components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import LoaderContainer from "src/components/Loader/LoaderContainer.svelte";
	import PageHeader from "src/components/PageHeader/PageHeader.svelte";
	import {
		collateVideoUrls,
		createQuotationOrEditCase,
		exportCaseByCaseNo,
		getCaseDetails,
		patchCaseStatus,
		postVideoUrlToCase,
		submitInvestigation,
	} from "src/services/fmsTasking";
	import {
		damageReviewedData,
		errorMessage,
		loading,
		toastMessage,
	} from "src/store/store";
	import { scrollToTop, splitCamelCaseAndCapitalize } from "src/utils/misc";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import {
		zendeskTicketId,
		linkToCases,
		dateReported,
		attribution,
		damageCause,
		damageCauseType,
		staffName,
		staffContact,
		thirdPartyName,
		thirdPartyContact,
		caseCreationForm,
		caseType,
		accidentGIAReportNo,
		claimType,
		selectedSides,
		liabilityType,
		noOfRepairDays,
		actualRepairCost,
		remarks,
		workshopInvoiceFiles,
		additionalPhotoFiles,
		additionalMediaFiles,
		incidentDateTime,
		resetFields,
		vqp,
		l1Remarks,
		tnsRemarks,
	} from "src/validators/CaseCreation";
	import { getVehicleDetails } from "src/services/vehicle";
	import CustomButton from "src/components/CustomButton/CustomButton.svelte";
	import {
		caseClosingReasonOptions,
		costBreakdownInitialValues,
	} from "src/utils/options";
	import {
		convert,
		convertToUtc,
		momentFormat,
		pickerFormat,
	} from "src/services/helpers";
	import { navigate } from "svelte-routing";
	import ConfirmModal from "src/components/CustomModal/ConfirmModal.svelte";
	import CaseActivities from "./CaseActivities.svelte";
	import Tabs from "src/components/Tabs/Tabs.svelte";
	import About from "./About.svelte";
	import { onDestroy } from "svelte";

	export let id;

	let activeModalKey = null;
	let caseDetails = {};
	let vehicleDetails = {};
	let state = "view"; // possible values: "view", "edit", "create"
	let existingWorkshopFiles = [];
	let existingAdditionalMedia = [];
	let costBreakdownItems = [];
	let videoFiles = [];
	let activeTabValue = 1;

	const items = [
		{
			label: "About",
			value: 1,
			component: About,
		},
		{
			label: "Activities",
			value: 2,
			component: CaseActivities,
		},
	];

	$: isView = state === "view";

	$: topButtonConfigs = [
		{
			id: "edit",
			show:
				isView &&
				["Approving", "PreBilling"].includes(caseDetails.status),
			props: {
				icon: "/assets/icon-pen.svg",
				outline: true,
				color: "secondary",
				onClick: enterEditMode,
			},
		},
		{
			id: "export",
			show: caseDetails.status === "PreBilling" && state !== "edit",
			props: {
				buttonText: "Export",
				outline: true,
				color: "secondary",
				onClick: () => exportCaseByCaseNo([caseDetails.caseNo]),
			},
		},
		{
			id: "close",
			show:
				isView &&
				caseDetails.status !== "Closed" &&
				caseDetails.status !== "PreBilling" &&
				!(
					caseDetails.createdFrom === "Operator" &&
					caseDetails.status === "Quoting"
				),
			props: {
				buttonText: "Close",
				outline: true,
				color: "secondary",
				disabled:
					caseDetails.status === "Investigating"
						? investigatingInvalid
						: caseDetails.status === "Approving"
							? !$tnsRemarks.value
							: isInvalid,
				onClick: () => openModal("CLOSE_CASE"),
			},
		},
		{
			id: "approve",
			show: isView && caseDetails.status === "Approving",
			props: {
				buttonText: "Approve",
				class: "themeButton primary",
				onClick: () => openModal("APPROVE"),
				disabled: !$tnsRemarks.value,
			},
		},
	];

	$: bottomButtonConfigs = [
		{
			id: "back",
			show: ["Quoting", "Investigating"].includes(caseDetails.status),
			props: {
				buttonText: "Back",
				outline: true,
				onClick: () => cancelButtonBack(caseDetails.status),
				color: "secondary",
			},
		},
		{
			id: "cancel",
			show:
				(caseDetails.status === "Approving" ||
					caseDetails.status === "PreBilling") &&
				isEdit,
			props: {
				buttonText: "Cancel",
				outline: true,
				onClick: cancelButtonRefresh,
				color: "secondary",
			},
		},
		{
			id: "saveDraft",
			show: ["Quoting"].includes(caseDetails.status),
			props: {
				buttonText: "Save as Draft",
				outline: true,
				onClick: () => openModal("SAVE_DRAFT"),
				color: "secondary",
				disabled:
					isInvalid ||
					userDamageInvalid ||
					staffDamageInvalid ||
					thirdPartyDamageInvalid ||
					FQInvalid,
			},
		},
		{
			id: "create",
			show: ["Quoting"].includes(caseDetails.status),
			props: {
				buttonText: "Create",
				onClick: () => openModal("CREATE_QUOTATION"),
				class: "themeButton primary",
				disabled:
					isInvalid ||
					userDamageInvalid ||
					staffDamageInvalid ||
					thirdPartyDamageInvalid ||
					FQInvalid ||
					$loading,
			},
		},
		{
			id: "submit",
			show: ["Investigating"].includes(caseDetails.status),
			props: {
				buttonText: "Submit",
				class: "themeButton primary",
				disabled: investigatingInvalid || $loading,
				onClick: () => openModal("SUBMIT_INVESTIGATION"),
			},
		},
		{
			id: "saveChanges",
			show:
				(caseDetails.status === "Approving" ||
					caseDetails.status === "PreBilling") &&
				isEdit,
			props: {
				buttonText: "Save Changes",
				class: "themeButton primary",
				disabled:
					isInvalid ||
					userDamageInvalid ||
					staffDamageInvalid ||
					thirdPartyDamageInvalid ||
					FQInvalid ||
					$loading ||
					editApprovingInvalid,
				onClick: () => openModal("SAVE_CHANGES"),
			},
		},
	];

	$: modalConfigs = {
		CREATE_QUOTATION: {
			header: "Create Quotation",
			bodyText:
				"You are about to create a quotation for this case. Are you sure you want to proceed?",
			action: handleCreateOrEditCase,
		},
		SAVE_DRAFT: {
			header: "Save as Draft",
			bodyText:
				"You are about to save this quotation as a draft. You can continue editing it later. Are you sure you want to proceed?",
			action: handleSaveAsDraft,
		},
		CLOSE_CASE: {
			header: "Close Case",
			bodyText:
				"You are about to close this case. This action cannot be undone. Please indicate a reason for closing.",
			action: patchCaseToClosed,
			// Special props for this modal only
			showDropdown: true,
			dropdownOptions: caseClosingReasonOptions,
			reasonHeader: "Please specify your reason for selecting ‘Others’",
			dropdownPlaceholder: "Select a reason for closing",
			icon: true,
		},
		SAVE_CHANGES: {
			header: "Save Changes",
			bodyText:
				"You are about to save the changes made to this case. Are you sure you want to proceed?",
			action: handleCreateOrEditCase,
		},
		APPROVE: {
			header: "Approve Case",
			bodyText: `You are about to approve this investigation. This action cannot be undone. Please ensure that the:\n\nProof is conclusive and correctly attributed to the suspect\nTotal revised amount is correct`,
			action: handleApproveCase,
			icon: true,
		},
		SUBMIT_INVESTIGATION: {
			header: "Submit Investigation",
			bodyText:
				"You are about to submit your investigation for review. This action cannot be undone after confirmation. Are you sure you want to proceed?",
			action: handleSubmitInvestigation,
		},
	};

	function enterEditMode() {
		state = "edit";
	}

	function enterViewMode() {
		state = "view";
	}

	function openModal(key) {
		activeModalKey = key;
	}

	function closeModal() {
		activeModalKey = null;
	}

	async function fetchCaseDetails() {
		const updateStore = (data, store, transformer = (v) => v) => {
			if (data || data === 0) {
				store.update((currentVal) => {
					currentVal.value = transformer(data);
					return currentVal;
				});
			}
		};
		try {
			loading.set(true);
			resetFields();

			const caseResponse = await getCaseDetails(id);
			caseDetails = caseResponse.data;

			const vehicleResponse = await getVehicleDetails(
				caseDetails.vehicleId
			);
			vehicleDetails = vehicleResponse.data;

			updateStore(caseDetails.incidentDateTime, incidentDateTime, (v) =>
				convert(v, momentFormat)
			);
			updateStore(caseDetails.collisionType, damageCauseType);
			updateStore(caseDetails.caseType, caseType);
			updateStore(caseDetails.claimType, claimType);
			updateStore(caseDetails.liabilityStatus, liabilityType);
			updateStore(caseDetails.repairDays, noOfRepairDays);
			updateStore(caseDetails.actualCost?.toFixed(2), actualRepairCost);
			updateStore(caseDetails.fqRemarks, remarks);
			updateStore(caseDetails.l1Remarks, l1Remarks);
			updateStore(caseDetails.tsRemarks, tnsRemarks);
			updateStore(caseDetails.attribution, attribution);
			updateStore(caseDetails.bookingNo, damageCause);
			updateStore(caseDetails.staffName, staffName);
			updateStore(caseDetails.staffContact, staffContact);
			updateStore(caseDetails.thirdPartyName, thirdPartyName);
			updateStore(caseDetails.thirdPartyContact, thirdPartyContact);
			updateStore(caseDetails.ticketId, zendeskTicketId);
			updateStore(caseDetails.linkToCase, linkToCases);
			updateStore(caseDetails.vqp, vqp);
			updateStore(caseDetails.createdAt, dateReported, (v) =>
				convert(v, momentFormat)
			);
			updateStore(caseDetails.accidentGIA, accidentGIAReportNo);
			updateStore(caseDetails.damageLocation, selectedSides);
			existingWorkshopFiles = caseDetails.workshopInvoices || [];
			existingAdditionalMedia = caseDetails.additionalPhotos || [];

			costBreakdownItems = costBreakdownInitialValues.map((item) => ({
				...item,
				value: undefined,
			}));

			if (
				caseDetails.priceQuotation &&
				caseDetails.priceQuotation.length > 0
			) {
				costBreakdownItems = caseDetails.priceQuotation.map((item) => ({
					item: item.item,
					value: item.cost,
				}));
			}
		} catch (error) {
			console.error("Error fetching case details:", error);
		} finally {
			loading.set(false);
		}
	}

	function appendFormDataFields() {
		const formData = new FormData();
		if ($attribution.value === "User") {
			// User
			formData.append("bookingNo", $damageCause.value);
		}
		if ($attribution.value === "Staff") {
			// Staff
			formData.append("staffName", $staffName.value);
			formData.append("staffContact", $staffContact.value.toString());
		}
		if ($attribution.value === "ThirdParty") {
			// ThirdParty
			formData.append("thirdPartyName", $thirdPartyName.value);
			formData.append(
				"thirdPartyContact",
				$thirdPartyContact.value.toString()
			);
		}
		if ($zendeskTicketId.value) {
			formData.append("TicketId", $zendeskTicketId.value);
		}
		if (
			$accidentGIAReportNo.value &&
			($caseType.value?.value || $caseType.value) === "Accident"
		) {
			formData.append("GIAReportNo", $accidentGIAReportNo.value);
		}

		formData.append("FQRemarks", $remarks.value);
		formData.append("L1Remarks", $l1Remarks.value);
		formData.append("TSRemarks", $tnsRemarks.value);

		formData.append(
			"actualCost",
			parseFloat($actualRepairCost.value).toFixed(2)
		);
		formData.append(
			"IncidentDateTime",
			convertToUtc($incidentDateTime.value, pickerFormat)
		);
		formData.append("CaseType", $caseType.value.value || $caseType.value);

		$selectedSides.value.forEach((side, idx) => {
			formData.append(`damageLocation[${idx}]`, side);
		});
		formData.append(
			"liabilityStatus",
			$liabilityType.value.value || $liabilityType.value
		);
		formData.append("repairDays", parseInt($noOfRepairDays.value) || 0);
		formData.append(
			"ClaimType",
			$claimType.value.value || $claimType.value
		);
		formData.append(
			"collisionType",
			$damageCauseType.value?.value || $damageCauseType.value
		);
		formData.append("attribution", $attribution.value);
		formData.append("remarks", $remarks.value);

		$linkToCases.value?.forEach((caseId, idx) => {
			formData.append(`LinkToCase[${idx}]`, caseId);
		});
		costBreakdownItems.forEach((item, idx) => {
			if (item.value !== undefined) {
				formData.append(`PriceQuotation[${idx}].item`, item.item);
				formData.append(
					`PriceQuotation[${idx}].cost`,
					item.value ? parseFloat(item.value).toFixed(2) : 0
				);
			}
		});
		formData.append(
			"finalAmount",
			costBreakdownItems
				.reduce((total, { value }) => total + (value || 0), 0)
				.toFixed(2)
		);
		formData.append("vqp", $vqp.value);
		$workshopInvoiceFiles.value.forEach((file) => {
			if (file instanceof File) {
				formData.append("WorkshopInvoice", file, file.name);
			}
		});
		$additionalMediaFiles.value.forEach((file) => {
			if (file.type.includes("video/")) {
				// video logic here
				videoFiles.push(file);
			} else {
				formData.append("AdditionalPhotos", file, file.name);
			}
		});

		return formData;
	}

	async function handleSaveAsDraft() {
		const formData = appendFormDataFields();
		try {
			loading.set(true);
			const res = await createQuotationOrEditCase(id, formData);
			if (res.status === 204) {
				state = "view";
				if (videoFiles.length > 0) {
					const urlArrs = await collateVideoUrls(videoFiles);
					if (urlArrs.length > 0) {
						await postVideoUrlToCase(id, urlArrs);
					}
				}
				toastMessage.set({
					message: `Draft saved`,
					type: "success",
				});
				resetFields();
				videoFiles = [];
				navigate("/issues/case-management?tab=Quoting");
			}
		} catch (err) {
			console.error(err);
			scrollToTop();
			throw err;
		} finally {
			loading.set(false);
			closeModal();
		}
	}

	async function handleCreateOrEditCase() {
		const formData = appendFormDataFields();
		try {
			loading.set(true);
			const res = await createQuotationOrEditCase(id, formData);
			if (res.status === 204) {
				state = "view";
				if (videoFiles.length > 0) {
					const urlArrs = await collateVideoUrls(videoFiles);
					if (urlArrs.length > 0) {
						await postVideoUrlToCase(id, urlArrs);
					}
				}
				if (caseDetails.status === "Quoting") {
					const nextStatus =
						caseDetails.createdFrom === "Operator"
							? "Investigating"
							: "PreBilling";
					await patchCaseStatus(id, { status: nextStatus });

					closeModal();
					toastMessage.set({
						message: `Quotation successfully created.`,
						type: "success",
					});
					const nextTab =
						caseDetails.createdFrom === "Operator"
							? "Investigating"
							: "PreBilling";
					navigate(`/issues/case-management?tab=${nextTab}`);
				} else {
					toastMessage.set({
						message: `Changes successfully saved.`,
						type: "success",
					});
					fetchCaseDetails();
				}

				resetFields();
				videoFiles = [];
			}
		} catch (err) {
			console.error(err);
			scrollToTop();
		} finally {
			loading.set(false);
			closeModal();
		}
	}

	async function patchCaseToClosed(reason, otherReason) {
		try {
			loading.set(true);
			const formData = appendFormDataFields();
			const createQuotationOrEditCaseRes =
				await createQuotationOrEditCase(id, formData);
			if (createQuotationOrEditCaseRes.status === 204) {
				// proceed to patch status only if the create/edit case is successful
				const res = await patchCaseStatus(id, {
					status: "Closed",
					reason: reason === "Others" ? otherReason : reason,
				});
				if (res.status === 200) {
					if (videoFiles.length > 0) {
						const urlArrs = await collateVideoUrls(videoFiles);
						if (urlArrs.length > 0) {
							await postVideoUrlToCase(id, urlArrs);
						}
					}
					resetFields();
					videoFiles = [];
					navigate("/issues/case-management?tab=Closed");
					toastMessage.set({
						message: `Case successfully closed.`,
						type: "success",
					});
				}
			}
		} catch (err) {
			console.error(err);
			scrollToTop();
		} finally {
			loading.set(false);
			closeModal();
		}
	}

	async function flatMapDamageImages() {
		const damagedImages = $damageReviewedData.flatMap(
			(booking) => booking.images || []
		);
		return damagedImages;
	}

	async function handleSubmitInvestigation() {
		try {
			loading.set(true);
			const data = {
				incidentDateTime: convertToUtc(
					$incidentDateTime.value,
					pickerFormat
				),
				attribution: $attribution.value,
				vehicleId: caseDetails.vehicleId,
				collisionType:
					$damageCauseType.value?.value || $damageCauseType.value,
				l1Remarks: $l1Remarks.value,
				vqp: parseInt($vqp.value),
			};

			if ($attribution.value === "User") {
				data["bookingNo"] = $damageCause.value;
			}
			if ($attribution.value === "Staff") {
				data["staffName"] = $staffName.value;
				data["staffContact"] = $staffContact.value.toString();
			}
			if ($attribution.value === "ThirdParty") {
				data["thirdPartyName"] = $thirdPartyName.value;
				data["thirdPartyContact"] = $thirdPartyContact.value.toString();
			}
			if ($damageReviewedData.length > 0) {
				const damagedImages = await flatMapDamageImages();
				data["highlightedDamagedSides"] = damagedImages.map(
					({
						damageAttribute,
						imgSide,
						imgUrl,
						isHighlighted,
						...rest
					}) => ({
						...rest,
						side: imgSide,
						url: imgUrl,
						isHighlighted: damageAttribute === "new-damage",
					})
				);
			}

			const res = await submitInvestigation(id, data);
			if (res.status === 204) {
				toastMessage.set({
					message: `Investigation successfully submitted.`,
					type: "success",
				});
				resetFields();
				navigate("/issues/case-management?tab=Approving");
				localStorage.removeItem("damageReviewedData");
			}
		} catch (error) {
			console.log(error);
			scrollToTop();
		} finally {
			loading.set(false);
			closeModal();
		}
	}

	async function handleApproveCase() {
		try {
			loading.set(true);
			const formData = appendFormDataFields();
			const createQuotationOrEditCaseRes =
				await createQuotationOrEditCase(id, formData);
			if (createQuotationOrEditCaseRes.status === 204) {
				const res = await patchCaseStatus(id, {
					status: "PreBilling",
				});
				if (res.status === 200) {
					toastMessage.set({
						message: `Case successfully approved.`,
						type: "success",
					});
					resetFields();
					navigate("/issues/case-management?tab=PreBilling");
				}
			}
		} catch (error) {
			console.error(error);
			scrollToTop();
		} finally {
			loading.set(false);
			closeModal();
		}
	}

	function cancelButtonBack(status) {
		navigate(`/issues/case-management?tab=${status}`, { replace: true });
	}

	function cancelButtonRefresh() {
		resetFields();
		fetchCaseDetails();
		enterViewMode();
	}

	$: if (id) {
		fetchCaseDetails();
	}

	if (typeof window !== "undefined") {
		window.addEventListener("popstate", () => {
			resetFields();
		});
	}

	$: isInvalid =
		!$caseType.value ||
		!$claimType.value ||
		!$liabilityType.value ||
		(!$noOfRepairDays.value && $noOfRepairDays.value !== 0) ||
		(!$actualRepairCost.value && $actualRepairCost.value !== 0) ||
		!$remarks.value ||
		(!$vqp.value && parseInt($vqp.value)) ||
		!$incidentDateTime.value;

	$: FQInvalid =
		caseDetails.createdFrom === "FQ" &&
		(!$incidentDateTime.value ||
			!$attribution.value ||
			!$damageCauseType.value);

	$: userDamageInvalid = $attribution.value === "User" && !$damageCause.value;
	$: staffDamageInvalid =
		$attribution.value === "Staff" &&
		(!$staffName.value || !$staffContact.value);
	$: thirdPartyDamageInvalid =
		$attribution.value === "ThirdParty" &&
		(!$thirdPartyName.value || !$thirdPartyContact.value);

	$: investigatingInvalid =
		caseDetails.status === "Investigating" &&
		(userDamageInvalid ||
			staffDamageInvalid ||
			thirdPartyDamageInvalid ||
			(!$vqp.value && parseInt($vqp.value)) ||
			!$damageCauseType.value ||
			!$incidentDateTime.value ||
			!$l1Remarks.value);

	$: editApprovingInvalid =
		!$tnsRemarks.value &&
		state === "edit" &&
		caseDetails.status === "Approving";
	$: isEdit = state === "edit";
	$: showTopButtons = topButtonConfigs.filter((b) => b.show);
	$: showBottomButtons = bottomButtonConfigs.filter((a) => a.show);
	$: currentModal = activeModalKey ? modalConfigs[activeModalKey] : null;

	onDestroy(() => {
		localStorage.removeItem("damageReviewedData");
		resetFields();
	});
</script>

<LoaderContainer>
	<CustomBreadcrumb
		links={[
			{ label: "Case Management", to: "/issues/case-management" },
			{ label: caseDetails?.caseNo },
			...(state === "edit" ? [{ label: "Edit Case" }] : []),
		]}
	/>
	<Row>
		<Col>
			<ErrorMessageHandler message={$errorMessage} />
			<div class="d-flex align-items-center justify-content-between mb-3">
				<div class="d-flex">
					<PageHeader title={caseDetails.caseNo} />
					<span
						class={`status-bar ${
							caseDetails.status !== "Closed"
								? "status-bar-pending"
								: "status-bar-gray"
						}`}
						>{splitCamelCaseAndCapitalize(caseDetails.status)}</span
					>
				</div>
				<div class="d-flex flex-end">
					{#each showTopButtons as btn (btn.id)}
						<CustomButton
							{...btn.props}
							on:click={btn.props.onClick}
						/>
					{/each}
				</div>
			</div>

			<Tabs
				{items}
				bind:activeTabValue
				{caseDetails}
				{vehicleDetails}
				{zendeskTicketId}
				{linkToCases}
				{state}
				{dateReported}
				{attribution}
				{damageCause}
				{damageCauseType}
				{staffName}
				{staffContact}
				{thirdPartyName}
				{thirdPartyContact}
				{incidentDateTime}
				form={caseCreationForm}
				{caseType}
				{accidentGIAReportNo}
				{claimType}
				{selectedSides}
				{liabilityType}
				{noOfRepairDays}
				{actualRepairCost}
				{remarks}
				{costBreakdownItems}
				{workshopInvoiceFiles}
				{additionalPhotoFiles}
				{existingAdditionalMedia}
				{existingWorkshopFiles}
				{additionalMediaFiles}
				{id}
				caseNo={caseDetails.caseNo}
				{vqp}
				{l1Remarks}
				{tnsRemarks}
			/>

			<Row class="mt-3">
				<Col class="d-flex justify-content-end gap-3">
					{#if activeTabValue !== 2}
						{#each showBottomButtons as bottomBtn (bottomBtn.id)}
							<CustomButton
								{...bottomBtn.props}
								on:click={bottomBtn.props.onClick}
							/>
						{/each}
					{/if}
				</Col>
			</Row>
		</Col>
	</Row>
</LoaderContainer>
{#if currentModal}
	<ConfirmModal
		open={true}
		toggle={closeModal}
		buttonColor="primary"
		buttonText="Confirm"
		outline={false}
		handleSubmit={currentModal.action}
		header={currentModal.header}
		bodyText={currentModal.bodyText}
		{...currentModal}
	/>
{/if}
